/**
 * Section Card Component
 * Displays a single prompt section with edit/delete actions
 */

import { useState } from 'react';
import { GripVertical, Edit2, Trash2, ToggleLeft, ToggleRight, ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import type { PromptSection, SectionType } from '@/types/promptTemplates';
import { SECTION_TYPE_INFO } from '@/types/promptTemplates';

interface SectionCardProps {
  section: PromptSection;
  isDragging?: boolean;
  onEdit: (section: PromptSection) => void;
  onDelete: (sectionId: string) => void;
  onToggle: (sectionId: string, enabled: boolean) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export function SectionCard({
  section,
  isDragging,
  onEdit,
  onDelete,
  onToggle,
  dragHandleProps,
}: SectionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const typeInfo = SECTION_TYPE_INFO[section.type as SectionType];

  return (
    <div
      className={clsx(
        'bg-white border rounded-lg transition-all',
        isDragging ? 'shadow-lg border-primary-300' : 'border-gray-200 hover:border-gray-300',
        !section.enabled && 'opacity-60'
      )}
    >
      <div className="flex items-center gap-3 p-3">
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="w-5 h-5" />
        </div>

        {/* Order Badge */}
        <div className="flex-shrink-0 w-10 h-8 flex items-center justify-center bg-gray-100 rounded text-sm font-mono text-gray-600">
          {section.order}
        </div>

        {/* Section Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">{section.name}</span>
            <span className={clsx('px-2 py-0.5 text-xs rounded-full', typeInfo?.color || 'bg-gray-100 text-gray-600')}>
              {typeInfo?.label || section.type}
            </span>
          </div>
          <div className="text-sm text-gray-500 truncate">{section.source}</div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Expand/Collapse */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {/* Toggle */}
          <button
            onClick={() => onToggle(section.id, !section.enabled)}
            className={clsx(
              'p-1.5 rounded transition-colors',
              section.enabled
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-400 hover:bg-gray-100'
            )}
            title={section.enabled ? 'Disable' : 'Enable'}
          >
            {section.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          </button>

          {/* Edit */}
          <button
            onClick={() => onEdit(section)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>

          {/* Delete */}
          <button
            onClick={() => onDelete(section.id)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-gray-100 mt-2">
          <div className="space-y-2 text-sm">
            {section.description && (
              <div>
                <span className="text-gray-500">Description:</span>
                <span className="ml-2 text-gray-700">{section.description}</span>
              </div>
            )}
            {section.wrap_tag && (
              <div>
                <span className="text-gray-500">Wrap Tag:</span>
                <code className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-700">
                  &lt;{section.wrap_tag}&gt;
                </code>
              </div>
            )}
            {section.template && (
              <div>
                <span className="text-gray-500">Template:</span>
                <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                  {section.template}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
